import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useStudio } from "@/context/StudioContext";
import { LoadingState, ErrorState, EmptyState } from "@/components/States";
import { StageBadge, StatusBadge } from "@/lib/projectMeta";
import { Clapperboard, CheckCircle2 } from "lucide-react";

function ProjectList({ title, eyebrow, description, filter, tab, icon: Icon, testid }) {
  const { selectedChannelId } = useStudio();
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setError(null);
    try {
      const { data } = await api.get(`/projects${selectedChannelId ? `?channel_id=${selectedChannelId}` : ""}`);
      setProjects(filter ? data.filter(filter) : data);
    } catch (e) { setError(apiError(e)); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only on channel change; `filter` is applied client-side
  }, [selectedChannelId]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!projects) return <LoadingState />;

  return (
    <div className="animate-in-up" data-testid={testid}>
      <div className="mb-8">
        <p className="text-[11px] font-semibold tracking-widest uppercase text-violet-400 mb-1">{eyebrow}</p>
        <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-slate-50">{title}</h1>
        <p className="text-sm text-slate-400 mt-2">{description}</p>
      </div>
      {projects.length === 0 ? (
        <EmptyState icon={Icon} title="Nothing here yet" description="Projects will appear here as they reach this stage." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => (
            <button key={p.id} onClick={() => navigate(`/projects/${p.id}?tab=${tab}`)} data-testid={`project-tile-${p.id}`}
              className="text-left bg-[#111827] border border-slate-800 rounded-xl p-5 hover:border-violet-500/40 transition-colors">
              <div className="flex items-center justify-between mb-2"><StageBadge stage={p.stage} /><StatusBadge status={p.stage_status} /></div>
              <h3 className="font-display font-semibold text-slate-100 mb-1">{p.title}</h3>
              <p className="text-xs text-slate-500">{p.channel_name} · {p.scene_count} scenes · {p.render_count} render(s)</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function Studio() {
  return <ProjectList testid="studio-page" eyebrow="Production" title="Production Studio" icon={Clapperboard} tab="render"
    description="Scene-based rendering on the server-side engine proven in Phase 0. Open a project to assemble scenes and render."
    filter={null} tab2 />;
}

export function Review() {
  return <ProjectList testid="review-page" eyebrow="Production" title="Review & Approvals" icon={CheckCircle2} tab="review"
    description="Human-in-the-loop approval. Approval is pinned to a specific render — any content change invalidates it."
    filter={(p) => ["render", "review", "approved", "scheduled"].includes(p.stage)} />;
}
